import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Card } from '@shared/ui/Card';
import { Table } from '@shared/ui/Table';
import { Badge } from '@shared/ui/Badge';
import { Skeleton } from '@shared/ui/Skeleton';
import {
  RegistryBar,
  RegistryEmpty,
  RegistryExportButton,
  RegistryFilterSelect,
  RegistryToolbar,
} from '@shared/ui/RegistryShell';
import { Button } from '@shared/ui/Button';
import {
  apiErrorText,
  useCloseDeadlineMutation,
  useDeleteDeadlineMutation,
  useListDeadlinesQuery,
  useReopenDeadlineMutation,
} from '@shared/api';
import { deadlineStatusLabel } from '@shared/lib/statusLabels';
import { downloadCsv } from '@shared/lib/downloadCsv';
import { useAppSelector } from '@app/hooks';
import { useAgentPermission } from '@entities/user';
import { Tab, Tabs } from './contracts/styles';

const SCOPE_LABELS: Record<string, string> = {
  contract: 'Договор',
  claim: 'Претензия',
  correspondence: 'Письмо',
  gov: 'Госорган',
};

const KIND_LABELS: Record<string, string> = {
  claim_response: 'Срок ответа на претензию',
  work_start: 'Срок начала работ',
  work_end: 'Срок окончания работ',
  warranty: 'Гарантийный срок',
  prolong: 'Пролонгация / расторжение',
  force_majeure_notice: 'Уведомление о форс-мажоре',
  payment: 'Срок оплаты',
  procedural: 'Процессуальный срок',
  answer: 'Срок ответа',
  statute_of_limitations: 'Срок исковой давности',
};

const SCOPE_OPTIONS = [
  { value: '', label: 'Все области' },
  { value: 'contract', label: 'Договоры' },
  { value: 'claim', label: 'Претензии' },
  { value: 'correspondence', label: 'Корреспонденция' },
  { value: 'gov', label: 'Госорганы' },
];

const KIND_OPTIONS = [
  { value: '', label: 'Любой тип' },
  { value: 'claim_response', label: 'Ответ на претензию' },
  { value: 'work_start', label: 'Начало работ' },
  { value: 'work_end', label: 'Окончание работ' },
  { value: 'warranty', label: 'Гарантия' },
  { value: 'prolong', label: 'Пролонгация / расторжение' },
  { value: 'force_majeure_notice', label: 'Форс-мажор' },
  { value: 'payment', label: 'Оплата' },
  { value: 'procedural', label: 'Процессуальный' },
  { value: 'answer', label: 'Ответ' },
  { value: 'statute_of_limitations', label: 'Исковая давность' },
];

const WITHIN_OPTIONS = [
  { value: 7, label: 'Ближайшие 7 дней' },
  { value: 14, label: 'Ближайшие 14 дней' },
  { value: 30, label: 'Ближайшие 30 дней' },
  { value: 90, label: 'Ближайшие 90 дней' },
  { value: 365, label: 'Ближайший год' },
];

/** Вкладки реестра. Состояние живёт в адресе (`?state=overdue`), чтобы
 *  красная цифра «Просрочено» на главной Юриста вела прямо в просроченные. */
type DeadlineState = 'active' | 'overdue' | 'closed';

const STATE_TABS: { value: DeadlineState; label: string }[] = [
  { value: 'active', label: 'Активные' },
  { value: 'overdue', label: 'Просроченные' },
  { value: 'closed', label: 'Выполненные' },
];

function parseState(raw: string | null): DeadlineState {
  return raw === 'overdue' || raw === 'closed' ? raw : 'active';
}

const EMPTY_TEXT: Record<DeadlineState, string> = {
  active: 'Активных сроков в выбранном окне нет.',
  overdue: 'Просроченных сроков нет.',
  closed: 'Выполненных сроков пока нет.',
};

const Note = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const DocLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-weight: 500;
  &:hover {
    text-decoration: underline;
  }
`;

const DocSubtitle = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: 2px;
`;

const DocFallback = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 12px;
`;

/** Примечание бывает в абзац: без обрезки одна строка занимала пол-экрана.
 *  Три строки в таблице, целиком — во всплывающей подсказке. */
const NoteText = styled.div`
  min-width: 220px;
  max-width: 380px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Actions = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const ActionError = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.status.danger};
`;

function formatDt(value: string): string {
  return new Date(value).toLocaleString('ru-RU');
}

function daysLeft(value: string): number {
  const ms = new Date(value).getTime() - Date.now();
  return Math.round(ms / 86_400_000);
}

export function Deadlines() {
  const { can_write } = useAgentPermission('statut');
  const [searchParams, setSearchParams] = useSearchParams();
  const listState = parseState(searchParams.get('state'));
  const [withinDays, setWithinDays] = useState(30);
  const [scopeFilter, setScopeFilter] = useState('');
  const [kindFilter, setKindFilter] = useState('');

  function setListState(next: DeadlineState) {
    const params = new URLSearchParams(searchParams);
    if (next === 'active') params.delete('state');
    else params.set('state', next);
    setSearchParams(params, { replace: true });
  }

  const { data, isLoading, isFetching } = useListDeadlinesQuery({
    within_days: withinDays,
    scope: scopeFilter || undefined,
    kind: kindFilter || undefined,
    state: listState,
  });

  const items = data?.items ?? [];

  const [deleteDeadline, { isLoading: isDeleting }] = useDeleteDeadlineMutation();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [closeDeadline] = useCloseDeadlineMutation();
  const [reopenDeadline] = useReopenDeadlineMutation();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  async function onDelete(id: string) {
    // Без confirm-диалога: запись сразу пропадает из реестра благодаря
    // optimistic update в deleteDeadline mutation. Если сервер ответит
    // ошибкой — RTK Query сам откатит, юрист увидит строку обратно.
    setDeletingId(id);
    try {
      await deleteDeadline(id).unwrap();
    } catch {
      // Молча — оптимистичный патч уже откатился. Никаких алертов.
    } finally {
      setDeletingId(null);
    }
  }

  async function onToggleDone(id: string, done: boolean) {
    setBusyId(id);
    setActionError(null);
    try {
      if (done) await closeDeadline(id).unwrap();
      else await reopenDeadline(id).unwrap();
    } catch (err) {
      setActionError(
        apiErrorText(err, done ? 'Не удалось отметить срок выполненным.' : 'Не удалось вернуть срок в работу.'),
      );
    } finally {
      setBusyId(null);
    }
  }

  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const [isExporting, setIsExporting] = useState(false);
  async function onExport() {
    setIsExporting(true);
    try {
      await downloadCsv(
        '/agents/statut/deadlines/export.csv',
        {
          within_days: withinDays,
          scope: scopeFilter,
          kind: kindFilter,
          state: listState,
        },
        accessToken,
        'deadlines.csv',
      );
    } finally {
      setIsExporting(false);
    }
  }

  const filtered = Boolean(scopeFilter || kindFilter);

  return (
    <>
      <Tabs role="tablist">
        {STATE_TABS.map((t) => (
          <Tab
            key={t.value}
            type="button"
            role="tab"
            aria-selected={listState === t.value}
            $active={listState === t.value}
            onClick={() => setListState(t.value)}
          >
            {t.label}
          </Tab>
        ))}
      </Tabs>

      <Note>
        Сроки по договорам, претензиям, письмам и запросам госорганов. Исполнили срок — нажмите
        «Выполнено»: он перестанет считаться просроченным и уйдёт во «Выполненные». «Удалить» — для
        ошибочных записей и сроков удалённых документов.
      </Note>

      <RegistryToolbar>
        {listState === 'active' ? (
          <RegistryFilterSelect
            value={withinDays}
            onChange={(e) => setWithinDays(Number(e.target.value))}
            title="Временное окно"
          >
            {WITHIN_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </RegistryFilterSelect>
        ) : (
          <span />
        )}
        <RegistryFilterSelect
          value={scopeFilter}
          onChange={(e) => setScopeFilter(e.target.value)}
          title="Фильтр по области"
        >
          {SCOPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </RegistryFilterSelect>
        <RegistryFilterSelect
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          title="Фильтр по типу"
        >
          {KIND_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </RegistryFilterSelect>
        <span />
        <span />
      </RegistryToolbar>

      <RegistryBar>
        <span>
          {isLoading
            ? 'Загрузка…'
            : `Найдено: ${items.length}${isFetching ? ' · обновление…' : ''}`}
        </span>
        <RegistryExportButton
          type="button"
          onClick={onExport}
          disabled={isExporting || items.length === 0}
          $disabled={isExporting || items.length === 0}
          title="Скачать текущую выборку в CSV для Excel"
        >
          {isExporting ? 'Готовим…' : 'Экспорт CSV'}
        </RegistryExportButton>
      </RegistryBar>

      {actionError && <ActionError role="alert">{actionError}</ActionError>}

      <Card>
        <Table>
          <thead>
            <tr>
              <th>Срок</th>
              <th>Осталось</th>
              <th>Область</th>
              <th>Документ</th>
              <th>Тип</th>
              <th>Статус</th>
              <th>Примечание</th>
              <th style={{ textAlign: 'right' }}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={`sk-${i}`}>
                  <td>
                    <Skeleton $width={140} $height={14} />
                  </td>
                  <td>
                    <Skeleton $width={80} $height={14} />
                  </td>
                  <td>
                    <Skeleton $width={90} $height={20} $radius="999px" />
                  </td>
                  <td>
                    <Skeleton $width="60%" $height={14} />
                  </td>
                  <td>
                    <Skeleton $width="70%" $height={14} />
                  </td>
                  <td>
                    <Skeleton $width={90} $height={20} $radius="999px" />
                  </td>
                  <td>
                    <Skeleton $width="80%" $height={14} />
                  </td>
                  <td>
                    <Skeleton $width={60} $height={14} />
                  </td>
                </tr>
              ))
            ) : items.length === 0 ? (
              <tr>
                <RegistryEmpty colSpan={8}>
                  {filtered ? 'По заданным фильтрам сроков нет.' : EMPTY_TEXT[listState]}
                </RegistryEmpty>
              </tr>
            ) : (
              items.map((d) => {
                const days = daysLeft(d.due_at);
                const closed = d.status === 'closed';
                const urgent = !closed && days <= 3;
                const busy = busyId === d.id;
                return (
                  <tr key={d.id}>
                    <td>{formatDt(d.due_at)}</td>
                    <td
                      style={{
                        color: urgent ? 'var(--osnova-danger, #d04848)' : undefined,
                        fontWeight: urgent ? 600 : undefined,
                      }}
                    >
                      {closed
                        ? '—'
                        : days < 0
                          ? `просрочено на ${-days} дн.`
                          : days === 0
                            ? 'сегодня'
                            : `${days} дн.`}
                    </td>
                    <td>
                      <Badge>{SCOPE_LABELS[d.scope] ?? d.scope}</Badge>
                    </td>
                    <td>
                      {d.entity_url && d.entity_title ? (
                        <>
                          <DocLink to={d.entity_url}>{d.entity_title}</DocLink>
                          {d.entity_subtitle && <DocSubtitle>{d.entity_subtitle}</DocSubtitle>}
                        </>
                      ) : (
                        <DocFallback>—</DocFallback>
                      )}
                    </td>
                    <td>{KIND_LABELS[d.kind] ?? d.kind}</td>
                    <td>
                      <Badge>{deadlineStatusLabel(d.status)}</Badge>
                    </td>
                    <td>{d.note ? <NoteText title={d.note}>{d.note}</NoteText> : '—'}</td>
                    <td style={{ textAlign: 'right' }}>
                      <Actions>
                        <Button
                          $variant={closed ? 'ghost' : 'secondary'}
                          $size="sm"
                          onClick={() => onToggleDone(d.id, !closed)}
                          disabled={busy || !can_write}
                          title={
                            !can_write
                              ? 'Только чтение'
                              : closed
                                ? 'Отметили по ошибке — вернуть срок в работу'
                                : 'Срок исполнен — убрать из активных и просроченных'
                          }
                        >
                          {busy ? '…' : closed ? 'Вернуть в работу' : 'Выполнено'}
                        </Button>
                        <Button
                          $variant="ghost"
                          $size="sm"
                          onClick={() => onDelete(d.id)}
                          disabled={(isDeleting && deletingId === d.id) || !can_write}
                          title={
                            !can_write
                              ? 'Только чтение'
                              : 'Удалить срок (например, осиротевший после удаления договора)'
                          }
                        >
                          {isDeleting && deletingId === d.id ? '…' : 'Удалить'}
                        </Button>
                      </Actions>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </Card>
    </>
  );
}
